// empty-state — default skin module
// Token disposition: pass-through (no overrides)
// NO external imports — plain JavaScript only

export const styles = `
  :host {
    display: block;
    container-type: inline-size;
    background: var(--run-empty-state-bg, transparent);
    color: var(--run-empty-state-fg, var(--run-color-text-primary));
  }

  [part="root"] {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: var(--run-empty-state-gap, var(--run-space-semantic-xs));
    padding-block: var(--run-empty-state-padding-block, var(--run-space-semantic-lg));
    padding-inline: var(--run-empty-state-padding-inline, var(--run-space-semantic-md));
    background: var(--run-empty-state-background, var(--run-color-surface-default));
  }

  [part="root"][data-variant="horizontal"] {
    flex-direction: row;
    text-align: start;
    align-items: flex-start;
  }

  [part="root"][data-tone="subtle"] {
    background: var(--run-color-surface-subtle);
    border-radius: var(--run-radius-semantic-card);
  }

  [part="media-wrapper"] {
    max-inline-size: 100%;
    flex-shrink: 0;
  }

  [part="icon-wrapper"] {
    max-inline-size: 100%;
    flex-shrink: 0;
    color: var(--run-empty-state-icon-color, var(--run-color-text-tertiary));
    font-size: var(--run-empty-state-icon-size, var(--run-size-icon-xl));
  }

  [part="icon-wrapper"][data-icon-hidden] {
    display: none;
  }

  [part="heading-wrapper"] {
    color: var(--run-empty-state-title-color, var(--run-color-text-primary));
    font-size: var(--run-empty-state-title-font-size, var(--run-font-size-lg));
    font-weight: var(--run-empty-state-title-font-weight, var(--run-font-semantic-weight-strong));
  }

  [part="description-wrapper"] {
    max-inline-size: var(--run-empty-state-max-width, 40ch);
    color: var(--run-empty-state-body-color, var(--run-color-text-secondary));
    font-size: var(--run-empty-state-body-font-size, var(--run-font-size-base));
  }

  [part="actions-wrapper"] {
    display: inline-flex;
    flex-wrap: wrap;
    gap: var(--run-space-semantic-sm);
  }

  [part="root"][data-size="lg"] [part="media-wrapper"],
  [part="root"][data-size="lg"] [part="icon-wrapper"] {
    block-size: var(--run-empty-state-illustration-lg, 64px);
  }

  /* 480px literal per contract.md (--run-empty-state-collapse-breakpoint is
     informational only — size container queries cannot read custom properties) */
  @container (inline-size < 480px) {
    [part="root"][data-variant="horizontal"] {
      flex-direction: column;
      text-align: center;
      align-items: center;
    }
  }
`;

export function render(host) {
  const size = host.size ?? host.getAttribute('size') ?? 'md';
  const variant = host.variant ?? host.getAttribute('variant') ?? 'vertical';
  const headingLevel = host.headingLevel ?? host.getAttribute('heading-level') ?? '2';
  const tone = host.tone ?? host.getAttribute('tone') ?? 'neutral';
  const { _mediaSlotted, _iconSlotted } = host;

  const iconHiddenAttr = _mediaSlotted ? ' data-icon-hidden' : '';

  host.shadowRoot.innerHTML = `
    <div part="root" data-size="${size}" data-variant="${variant}" data-tone="${tone}">
      <div part="media-wrapper" aria-hidden="true">
        <slot name="media"></slot>
      </div>
      <div part="icon-wrapper" aria-hidden="true"${iconHiddenAttr}>
        <slot name="icon"></slot>
      </div>
      <div part="heading-wrapper">
        <h${headingLevel}>
          <slot name="heading"></slot>
        </h${headingLevel}>
      </div>
      <div part="description-wrapper">
        <slot></slot>
      </div>
      <div part="actions-wrapper">
        <slot name="actions"></slot>
      </div>
    </div>
  `;
}